import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Dices } from "lucide-react";
import { ADDON_CATEGORIES, ROLETA, IMAGES, formatBRL, slug } from "../data/menu";
import { Chapter } from "./Chapter";
import { AddonChip } from "./AddonChip";

export const Roleta = ({ onAdd }) => {
  const [picked, setPicked] = useState([]);
  const max = ROLETA.freeAddons;

  const toggle = (item, color) => {
    setPicked((prev) => {
      if (prev.some((p) => p.name === item.name))
        return prev.filter((p) => p.name !== item.name);
      if (prev.length >= max) {
        toast.error(`Você já escolheu os ${max} adicionais grátis`);
        return prev;
      }
      return [...prev, { name: item.name, color }];
    });
  };

  const handleAdd = () => {
    onAdd({
      title: "Roleta de Açaí (6 adicionais grátis)",
      details: picked.map((p) => p.name),
      unitPrice: ROLETA.price,
      qty: 1,
    });
    setPicked([]);
  };

  return (
    <div>
      <Chapter number="02" title="Roleta de Açaí" accent="#EC4899">
        A favorita da galera: escolha 6 adicionais grátis e receba aquela
        roleta gigante para compartilhar — ou não.
      </Chapter>

      <div className="grid gap-12 lg:grid-cols-[400px_1fr]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:sticky lg:top-24 lg:self-start"
        >
          <div className="relative overflow-hidden rounded-[2rem] border border-purple-500/25">
            <img
              src={IMAGES.roleta}
              alt="Roleta de açaí com diversos acompanhamentos"
              className="aspect-[5/4] w-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 bg-[#1E053B]/85 p-6 backdrop-blur-md">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-grotesk text-[11px] font-bold uppercase tracking-[0.25em] text-pink-400">
                    Para compartilhar
                  </p>
                  <p
                    data-testid="roleta-price"
                    className="mt-1 font-syne text-3xl font-extrabold text-white"
                  >
                    {formatBRL(ROLETA.price)}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  {Array.from({ length: max }).map((_, i) => (
                    <span
                      key={i}
                      data-testid={`roleta-slot-${i}`}
                      className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${
                        i < picked.length ? "bg-pink-400" : "bg-purple-500/30"
                      }`}
                    />
                  ))}
                </div>
              </div>
              <p
                data-testid="roleta-counter"
                className="mt-2 text-xs text-purple-200/70"
              >
                {picked.length} de {max} adicionais escolhidos
              </p>
              <button
                type="button"
                data-testid="roleta-add-button"
                onClick={handleAdd}
                disabled={picked.length !== max}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-pink-500 py-4 font-grotesk text-sm font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 enabled:hover:scale-[1.03] enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Dices className="h-4 w-4" />
                Adicionar roleta ao pedido
              </button>
            </div>
          </div>
        </motion.div>

        <div className="space-y-10">
          {ADDON_CATEGORIES.map((cat) => (
            <div key={cat.id}>
              <div className="mb-4 flex items-center gap-3">
                <span
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: cat.color }}
                />
                <h4 className="font-syne text-lg font-bold text-white">{cat.name}</h4>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {cat.items.map((item) => (
                  <AddonChip
                    key={item.name}
                    item={item}
                    color={cat.color}
                    showPrice={false}
                    active={picked.some((p) => p.name === item.name)}
                    testId={`roleta-addon-${slug(item.name)}`}
                    onToggle={() => toggle(item, cat.color)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
